import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  List,
  Plus,
  Rocket,
  SlidersHorizontal,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { ideasApi, ideaVideoStylesApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { IdeaDialog } from "@/components/ideas/IdeaDialog";
import { VideoStylesDialog } from "@/components/ideas/VideoStylesDialog";
import { ReceiptLightbox } from "@/components/expenses/ReceiptLightbox";

// Palette used to colour idea chips by video style — cycles deterministically
// off the style id so a given style always gets the same colour.
const CHIP_COLORS = [
  "bg-amber-500/25 text-amber-100 border-amber-500/50 hover:bg-amber-500/40",
  "bg-rose-500/25 text-rose-100 border-rose-500/50 hover:bg-rose-500/40",
  "bg-teal-500/25 text-teal-100 border-teal-500/50 hover:bg-teal-500/40",
  "bg-violet-500/25 text-violet-100 border-violet-500/50 hover:bg-violet-500/40",
  "bg-blue-500/25 text-blue-100 border-blue-500/50 hover:bg-blue-500/40",
  "bg-emerald-500/25 text-emerald-100 border-emerald-500/50 hover:bg-emerald-500/40",
];
function colorFor(id) {
  if (!id) return CHIP_COLORS[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return CHIP_COLORS[h % CHIP_COLORS.length];
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function fmtHumanDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function PlanningCalendarPage({ clientId = null, clientName = "", embedded = false } = {}) {
  const { user } = useAuth();
  const isStaff = !user?.client_id;
  const scoped = !!clientId;
  const [ideas, setIdeas] = useState([]);
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeIdea, setActiveIdea] = useState(null);
  const [stylesDialogOpen, setStylesDialogOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {url, filename, contentType}

  const reload = async () => {
    setLoading(true);
    try {
      const [ideasList, stylesList] = await Promise.all([
        ideasApi.list(clientId || undefined),
        ideaVideoStylesApi.list(),
      ]);
      setIdeas(ideasList || []);
      setStyles(stylesList || []);
    } catch (e) {
      toast.error("Failed to load planning calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const reloadStyles = async () => {
    try {
      const list = await ideaVideoStylesApi.list();
      setStyles(list || []);
    } catch {
      /* no-op */
    }
  };

  const openNew = () => {
    setActiveIdea(null);
    setDialogOpen(true);
  };
  const openIdea = (idea) => {
    setActiveIdea(idea);
    setDialogOpen(true);
  };

  // In scoped mode the dialog needs the client pre-set; we pass it through
  // via a "seed" object handed to IdeaDialog when creating a new idea.
  const newIdeaSeed = scoped
    ? { client_id: clientId, client_name: clientName }
    : null;

  // ── Calendar model ───────────────────────────────────────────────
  // Build a 6×7 grid starting on the Sunday before (or of) the 1st.
  const cells = useMemo(() => {
    const first = new Date(cursor);
    first.setDate(1);
    const firstDow = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(1 - firstDow);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [cursor]);

  const ideasByLaunchDate = useMemo(() => {
    const map = new Map();
    for (const idea of ideas) {
      const iso = idea.launch_date;
      if (!iso) continue;
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(idea);
    }
    return map;
  }, [ideas]);

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const todayISO = toISO(new Date());

  const wrapperClass = embedded
    ? "space-y-6"
    : "mx-auto max-w-7xl px-4 sm:px-8 py-8 space-y-6";

  return (
    <div className={wrapperClass}>
      {/* Header (hidden in embedded mode — parent supplies its own) */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-overline text-zinc-500 mb-1">Client Workspace</div>
            <h1 className="text-4xl sm:text-5xl font-heading font-light">
              Planning Calendar
            </h1>
            <p className="text-sm text-zinc-500 mt-2 max-w-xl">
              Brainstorm shoots, pin launch dates, and hand the winners off to
              production with one click.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isStaff && (
              <Button
                type="button"
                variant="outline"
                data-testid="planning-manage-styles-btn"
                onClick={() => setStylesDialogOpen(true)}
                className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" /> Video styles
              </Button>
            )}
            <Button
              type="button"
              data-testid="planning-new-idea-btn"
              onClick={openNew}
              className="bg-amber-500 text-black hover:bg-amber-400"
            >
              <Plus className="w-4 h-4 mr-2" /> New idea
            </Button>
          </div>
        </div>
      )}

      {/* Embedded header — inline title + action buttons on the right */}
      {embedded && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-lg text-zinc-200">Planning Calendar</h2>
            <span className="text-xs text-zinc-500">
              · {ideas.length} {ideas.length === 1 ? "idea" : "ideas"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isStaff && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="planning-manage-styles-btn"
                onClick={() => setStylesDialogOpen(true)}
                className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
              >
                <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Video styles
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              data-testid="planning-new-idea-btn"
              onClick={openNew}
              className="bg-amber-500 text-black hover:bg-amber-400"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New idea
            </Button>
          </div>
        </div>
      )}

      {/* View toggle + month nav */}
      <div className="flex items-center justify-between gap-3">
        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(v) => v && setView(v)}
          data-testid="planning-view-toggle"
          className="bg-zinc-900/60 border border-zinc-800 rounded-md p-0.5"
        >
          <ToggleGroupItem value="calendar" data-testid="planning-view-calendar" className="data-[state=on]:bg-amber-500 data-[state=on]:text-black text-zinc-400">
            <CalendarIcon className="w-4 h-4 mr-1.5" /> Calendar
          </ToggleGroupItem>
          <ToggleGroupItem value="list" data-testid="planning-view-list" className="data-[state=on]:bg-amber-500 data-[state=on]:text-black text-zinc-400">
            <List className="w-4 h-4 mr-1.5" /> List
          </ToggleGroupItem>
        </ToggleGroup>
        {view === "calendar" && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="planning-prev-month"
              onClick={() => {
                const d = new Date(cursor);
                d.setMonth(d.getMonth() - 1);
                setCursor(d);
              }}
              className="p-2 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-zinc-900"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm font-medium text-zinc-200 min-w-[10rem] text-center">
              {monthLabel}
            </div>
            <button
              type="button"
              data-testid="planning-next-month"
              onClick={() => {
                const d = new Date(cursor);
                d.setMonth(d.getMonth() + 1);
                setCursor(d);
              }}
              className="p-2 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-zinc-900"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const d = new Date();
                d.setDate(1);
                setCursor(d);
              }}
              className="border-zinc-800 text-zinc-300"
            >
              Today
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      ) : view === "calendar" ? (
        <CalendarGrid
          cells={cells}
          cursor={cursor}
          todayISO={todayISO}
          byDate={ideasByLaunchDate}
          onOpen={openIdea}
        />
      ) : (
        <IdeaList ideas={ideas} onOpen={openIdea} />
      )}

      {dialogOpen && (
        <IdeaDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          idea={activeIdea || newIdeaSeed}
          styles={styles}
          scopedClientId={clientId}
          onSaved={reload}
          onStylesChanged={reloadStyles}
          onLightbox={setLightbox}
        />
      )}

      <VideoStylesDialog
        open={stylesDialogOpen}
        onOpenChange={setStylesDialogOpen}
        styles={styles}
        onChanged={reloadStyles}
      />

      {/* Reuse the receipt lightbox for attachment previews. It only
          needs a shape with {receipt_filename, receipt_content_type}
          plus a projectId (unused for signed URL here — we bypass by
          providing the URL directly via a synthetic shape). */}
      {lightbox && (
        <ReceiptLightbox
          open={!!lightbox}
          onOpenChange={(v) => !v && setLightbox(null)}
          projectId="__idea__"
          expense={{
            id: "__idea__",
            receipt_filename: lightbox.filename,
            receipt_content_type: lightbox.contentType,
            _presignedUrl: lightbox.url,
          }}
        />
      )}
    </div>
  );
}

function CalendarGrid({ cells, cursor, todayISO, byDate, onOpen }) {
  const inMonth = (d) => d.getMonth() === cursor.getMonth();
  return (
    <div
      data-testid="planning-calendar-grid"
      className="rounded-md border border-zinc-800 bg-zinc-950/60 overflow-hidden"
    >
      <div className="grid grid-cols-7 border-b border-zinc-800 bg-zinc-900/40">
        {DOW.map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 text-center"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-fr">
        {cells.map((d, i) => {
          const iso = toISO(d);
          const dayIdeas = byDate.get(iso) || [];
          const isToday = iso === todayISO;
          const dimmed = !inMonth(d);
          return (
            <div
              key={i}
              data-testid={`planning-cell-${iso}`}
              className={`min-h-[7rem] border-r border-b border-zinc-800/70 p-1.5 relative ${
                dimmed ? "bg-zinc-950 opacity-40" : "bg-zinc-950/60"
              }`}
            >
              <div
                className={`text-[11px] font-mono ${
                  isToday
                    ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-black font-bold"
                    : "text-zinc-500"
                }`}
              >
                {d.getDate()}
              </div>
              <div className="mt-1 space-y-1">
                {dayIdeas.slice(0, 3).map((idea) => (
                  <button
                    key={idea.id}
                    type="button"
                    onClick={() => onOpen(idea)}
                    data-testid={`planning-chip-${idea.id}`}
                    className={`w-full text-left text-[11px] px-1.5 py-0.5 rounded border truncate transition-colors ${colorFor(
                      idea.video_style_id || idea.id
                    )} ${idea.launched_project_id ? "opacity-70" : ""}`}
                    title={`${idea.title} · ${idea.video_style_name || "no style"}`}
                  >
                    {idea.launched_project_id && "🚀 "}
                    {idea.title}
                  </button>
                ))}
                {dayIdeas.length > 3 && (
                  <div className="text-[10px] text-zinc-500 pl-1">
                    +{dayIdeas.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IdeaList({ ideas, onOpen }) {
  if (!ideas.length) {
    return (
      <div
        data-testid="planning-list-empty"
        className="border border-dashed border-zinc-800 rounded-md p-16 text-center"
      >
        <CalendarIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <div className="text-lg font-medium text-zinc-300 mb-1">No ideas yet</div>
        <div className="text-sm text-zinc-500">
          Click <span className="text-amber-400">+ New idea</span> to start planning.
        </div>
      </div>
    );
  }
  const sorted = [...ideas].sort((a, b) => (a.launch_date || "9").localeCompare(b.launch_date || "9"));
  return (
    <div
      data-testid="planning-list-table"
      className="rounded-md border border-zinc-800 overflow-hidden"
    >
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left px-4 py-2">Title</th>
            <th className="text-left px-4 py-2">Client</th>
            <th className="text-left px-4 py-2">Style</th>
            <th className="text-left px-4 py-2">Owner</th>
            <th className="text-left px-4 py-2">Start</th>
            <th className="text-left px-4 py-2">Launch</th>
            <th className="text-left px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800/70">
          {sorted.map((idea) => (
            <tr
              key={idea.id}
              data-testid={`planning-row-${idea.id}`}
              onClick={() => onOpen(idea)}
              className="hover:bg-zinc-900/40 cursor-pointer"
            >
              <td className="px-4 py-3">
                <div className="font-medium text-zinc-100">{idea.title}</div>
                {idea.notes && (
                  <div className="text-xs text-zinc-500 truncate max-w-md">{idea.notes}</div>
                )}
              </td>
              <td className="px-4 py-3 text-zinc-400">{idea.client_name || "—"}</td>
              <td className="px-4 py-3">
                {idea.video_style_name ? (
                  <span
                    className={`text-xs px-2 py-0.5 rounded border ${colorFor(idea.video_style_id || idea.id)}`}
                  >
                    {idea.video_style_name}
                  </span>
                ) : (
                  <span className="text-zinc-600">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-zinc-400">{idea.project_owner || "—"}</td>
              <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{fmtHumanDate(idea.start_date)}</td>
              <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{fmtHumanDate(idea.launch_date)}</td>
              <td className="px-4 py-3">
                {idea.launched_project_id ? (
                  <span
                    data-testid={`planning-status-launched-${idea.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                  >
                    <Rocket className="w-3 h-3" /> Launched
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded border bg-zinc-800/60 text-zinc-400 border-zinc-700">
                    Idea
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
